import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { FormBuilder, Modal } from "shared/components";
import { addTemporaryToast } from "shared/components/Toast";
import { copy } from "shared/assets";
import { Edit } from "shared/assets/icons";
import { dateStringToISO } from "shared/utils";
import { IAPIState, IFormButtons, IStatus } from "shared/types";
import { API_ENDPOINTS } from "shared/constants";

import {
  dataMetaSelector,
  dispatchGetBandAction,
  startTrial,
} from "Bands/state";
import { BAND_ACTIONS } from "Bands/types";

import { fields } from "./formData";
import { useClearActionMetaOnUnMount } from "Bands/utils";

const modalCopy = copy.adminDashboard.startTrial;

interface StartTrialModalProps {
  bandId: string;
  handleClose: () => void;
}

export const StartTrialModal = ({
  bandId,
  handleClose,
}: StartTrialModalProps) => {
  useClearActionMetaOnUnMount(BAND_ACTIONS.startTrial);

  const dispatch = useDispatch();
  const { api: apiMeta } = useSelector(
    dataMetaSelector(BAND_ACTIONS.startTrial)
  );

  const defaultValues = useMemo(() => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const joined = [day, month, year].join("/");

    return {
      start: joined,
      stop: "",
    };
  }, []);

  const buttons: IFormButtons = useMemo(
    () => ({
      primary: {
        "data-testid": "confirm_start_trial",
      },
      secondary: {
        onClick: handleClose,
        "data-testid": "cancel_start_trial",
      },
    }),
    [handleClose]
  );

  const onSubmit = (data: { start: string; stop: string }) => {
    dispatch(
      startTrial({
        bandId: API_ENDPOINTS.BAND(bandId),
        start: `${dateStringToISO(data.start)}T23:59:59+00:00`,
        stop: `${dateStringToISO(data.stop)}T23:59:59+00:00`,
      })
    );
  };

  const error = useMemo(() => !!apiMeta?.error, [apiMeta?.error]);

  useEffect(() => {
    if (apiMeta?.status === IAPIState.succeeded) {
      // refetch band to update subscription
      dispatchGetBandAction(dispatch, bandId);

      handleClose();

      dispatch(
        addTemporaryToast({
          message: modalCopy.success,
          status: IStatus.success,
        })
      );
    }
  }, [dispatch, apiMeta, handleClose, bandId]);

  return (
    <Modal handleClose={handleClose} title={modalCopy.title} icon={Edit}>
      <FormBuilder
        fields={fields}
        onSubmit={onSubmit}
        defaultValues={defaultValues}
        submitMode="isDirty"
        buttons={buttons}
        isSubmitting={apiMeta?.status === IAPIState.pending}
        apiErrorMessage={error}
        apiError={apiMeta?.error}
      />
    </Modal>
  );
};
